import { createSelector } from 'reselect';
import isNil from 'lodash.isnil';
import isEmpty from 'lodash.isempty';
import last from 'lodash.last';
import get from 'lodash.get';
import createSelectors from '../../utils/shared/selectorUtils';
import { selectors as dualLanguageSelectors } from '../../api/dualLanguage/dualLanguageReducer';
import { selectors as interpsSelectors } from '../../api/interps/shared/interpReducer';
import { selectors as studentSummarySelectors } from '../../api/studentSummary/shared/studentSummaryReducer';
import { selectors as enrollmentSelectors } from '../../api/enrollment/reading/enrollmentReducer';
import { selectors as resultSelectors } from '../../api/results/reading/resultReducer';
import { selectors as instructionSelectors } from '../../api/instruction/reading/instructionReducer';
import { selectors as pdfSelectors } from '../../api/pdfGeneration/shared/pdfReducer';
import { selectors as tokenSelectors } from '../../api/keycloak/tokenReducer';
import { selectors as featureReportSelectors } from '../../api/featureReport/featureReportReducer';
import {
    getAllStudentsStatuses,
    getAllStudentSummaryStatuses,
    getAmpNavConfig,
    getSelectedGradeCheckPointObject,
    getStudentIds,
    getStudentStatusesById,
    getTotalNumberOfStudents,
    getTrcSummaryData,
} from './rootHelpers';
import { getIsPrintIconDisabled } from '../../utils/shared/headerUtils';
import { GRADE } from '../../constants/shared/GradesConstants';
import { LAST_LOCATION_INDEX } from '../../constants/shared/CommonConstants';
import { filterResultsOfAdditionalMeasures } from '../../utils/shared/additionalMeasureUtils';

const { ALL_GRADES } = GRADE;

const selectors = {
    ...createSelectors(interpsSelectors, 'interps'),
    ...createSelectors(enrollmentSelectors, 'enrollment'),
    ...createSelectors(resultSelectors, 'results'),
    ...createSelectors(studentSummarySelectors, 'studentSummary'),
    ...createSelectors(instructionSelectors, 'instruction'),
    ...createSelectors(dualLanguageSelectors, 'dualLanguage'),
    ...createSelectors(pdfSelectors, 'pdf'),
    ...createSelectors(tokenSelectors, 'userInfo'),
    ...createSelectors(featureReportSelectors, 'featureReport'),
};

selectors.isDataReady = createSelector(
    [selectors.isResultDataFetched, selectors.isInterpDataFetched],
    (isResultDataFetched, isInterpDataFetched) => isResultDataFetched && isInterpDataFetched,
);

selectors.isPrintIconDisabled = createSelector(
    [
        selectors.isGradeYearsFetching,
        selectors.isPdfDownloading,
        selectors.isDataFetched,
        selectors.isDataReady,
    ],
    (isGradeYearsFetching, isPdfDownloading, isDataFetched, isDataReady) =>
        getIsPrintIconDisabled(isGradeYearsFetching, isPdfDownloading, isDataFetched, isDataReady),
);
selectors.getStudentsResultsCurrentYear = createSelector(
    [selectors.getChosenClassStudentsByGrade, selectors.getResults, selectors.getCurrentSchoolYear],
    (studentsByGrade, results, year) => ({ studentsByGrade, results, year }),
);
selectors.getAllStudentsStatuses = createSelector(
    [selectors.getStudentsResultsCurrentYear],
    ({ studentsByGrade, results, year }) => {
        const filteredResults = filterResultsOfAdditionalMeasures(results);
        return getAllStudentsStatuses(studentsByGrade, filteredResults, year);
    },
);

selectors.getAllStudentSummaryStatuses = createSelector(
    [selectors.getStudentsResultsCurrentYear, selectors.getAllStudentsGradeYears],
    ({ results, studentsByGrade }, allStudentsGradeYears) =>
        getAllStudentSummaryStatuses(results, allStudentsGradeYears, studentsByGrade),
);

selectors.getSelectedPeriod = createSelector(
    [selectors.getSelectedPeriod, selectors.getCurrentPeriod],
    (selectedPeriod, currentPeriod) => (isNil(selectedPeriod) ? currentPeriod : selectedPeriod),
);

selectors.getSelectedInstructionGrade = createSelector(
    [selectors.getSelectedInstructionGrade, selectors.getDefaultGradeInCurrentClass],
    (selectedInstructionGrade, defaultGradeInCurrentClass) =>
        isNil(selectedInstructionGrade) ? defaultGradeInCurrentClass : selectedInstructionGrade,
);

selectors.isSecureResultFound = createSelector(
    [selectors.getStudentsResultsCurrentYear, selectors.getSelectedInstructionGrade],
    ({ results }, selectedInstructionGrade) =>
        Object.keys(results).some(key =>
            results[key].results
                ?.filter(({ grade }) => grade === selectedInstructionGrade)
                .some(result => result.isSecure),
        ),
);

selectors.getSelectedGradeCheckPointObject = createSelector(
    [
        selectors.getCheckPoints,
        selectors.getSelectedInstructionGrade,
        selectors.isSecureResultFound,
        selectors.getWindowDatesForCurrentPeriod,
    ],
    (checkPoints, selectedInstructionGrade, isSecureFound, windowDates) =>
        getSelectedGradeCheckPointObject(
            checkPoints,
            selectedInstructionGrade,
            isSecureFound,
            windowDates,
        ),
);

selectors.getSelectedInstructionDate = createSelector(
    [selectors.getSelectedInstructionDate, selectors.getSelectedGradeCheckPointObject],
    (selectedInstructionDate, selectedCheckPoints) =>
        isNil(selectedInstructionDate) ? selectedCheckPoints.dates[0] : selectedInstructionDate,
);

selectors.getSelectedDualLanguageGrade = createSelector(
    [selectors.getSelectedDualLanguageGrade, selectors.getDefaultGradeInCurrentClass],
    (selectedDualLanguageGrade, defaultGradeInCurrentClass) =>
        isNil(selectedDualLanguageGrade) ? defaultGradeInCurrentClass : selectedDualLanguageGrade,
);

selectors.getEnrollmentInfo = createSelector(
    [selectors.getDistrictId, selectors.getSchoolId, selectors.getClassOrGroupId],
    (district, school, classe) => ({
        district,
        school,
        classe,
    }),
);

selectors.getSelectedGradeYear = createSelector(
    [selectors.getGradeYears, selectors.getSelectedGradeYear, selectors.isGradeYearsFetching],
    (gradeYears, selectedGradeYear, isGradeYearsFetching) =>
        isEmpty(selectedGradeYear) && !isGradeYearsFetching ? last(gradeYears) : selectedGradeYear,
);

selectors.getStudentStatusesForSelectedStudent = createSelector(
    [selectors.getResults, selectors.getSelectedStudentId],
    (results, studentId) => getStudentStatusesById(results, studentId),
);

selectors.getTrcSummaryData = createSelector(
    [
        selectors.getResults,
        selectors.getSelectedStudentId,
        selectors.getSelectedGradeYear,
        selectors.getToyPeriods,
    ],
    (results, studentId, selectedGradeYear, toyPeriods) =>
        getTrcSummaryData(results, studentId, selectedGradeYear, toyPeriods),
);

selectors.getCommonData = createSelector(
    [
        selectors.getLicensingInfo,
        selectors.getDistrictId,
        selectors.getSchoolId,
        selectors.getClassOrGroupId,
        selectors.getStudentIds,
        selectors.isDemoClass,
        selectors.getCurrentSchoolYear,
        selectors.getUserId,
    ],
    (licensingInfo, districtId, schoolId, classId, studentIds, isDemoClass, year, userId) => ({
        licensingInfo,
        districtId,
        schoolId,
        classId,
        studentIds,
        isDemoClass,
        year,
        userId,
    }),
);

selectors.getSelectedGrade = createSelector(
    [selectors.getSelectedGrade, selectors.getSelectedGradeByDefault],
    (selectedGrade, selectedGradeByDefault) =>
        isNil(selectedGrade) ? selectedGradeByDefault : selectedGrade,
);

selectors.getSelectedGradesForClassPerformance = createSelector(
    [selectors.getSelectedGrade, selectors.getGradesInCurrentClass],
    (selectedGrade, gradesInCurrentClass) =>
        selectedGrade === ALL_GRADES ? gradesInCurrentClass : Array.of(selectedGrade),
);

selectors.getTotalNumberOfStudents = createSelector(
    [selectors.getStudentIds, selectors.getChosenClassStudentsByGrade, selectors.getSelectedGrade],
    (studentIds, studentsByGrade, selectedGrade) =>
        getTotalNumberOfStudents(studentIds, studentsByGrade, selectedGrade),
);

selectors.getStudentsIdsByGrade = createSelector(
    [selectors.getStudentsResultsCurrentYear, selectors.getSelectedGrade],
    ({ results, year }, grade) => getStudentIds(results, year, grade),
);

selectors.getLocation = state => get(state, 'router.location');

selectors.getLastLocation = state =>
    get(state, ['router', `previousLocations`, LAST_LOCATION_INDEX, 'location']);

selectors.getAmpNavConfig = createSelector([selectors.getStaffConfig], staffConfig =>
    getAmpNavConfig(staffConfig),
);

export default selectors;
